import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import Link from 'next/link'
import { cva } from 'class-variance-authority'
import { ReactNode } from 'react'
import { cn } from '@/ui/utils/style'
import { Typography } from '../typography/Typography'
import { Badge, GradientBadge } from '../badge/Badge'

type TabItem<T extends string> = T | { id: T; label: string; icon?: ReactNode; disabled?: boolean }

interface TabSelectorProps<T extends string> {
  activeTab: T
  setActiveTab: (tab: T) => void
  tabs: readonly TabItem<T>[]
  className?: string
  containerClassName?: string
}

const buttonStyles = cva('border-b-2 pb-4 transition-colors disabled:pointer-events-none hover:text-foreground', {
  variants: {
    active: {
      true: 'border-primary text-primary',
      false: 'border-transparent text-link-inactive',
    },
  },
})

export function TabSelector<T extends string>({
  activeTab,
  setActiveTab,
  className,
  tabs,
  containerClassName,
}: TabSelectorProps<T>) {
  return (
    <div className={cn('flex gap-4', containerClassName)}>
      {tabs.map((tab) => {
        const tabId = typeof tab === 'string' ? tab : tab.id
        const tabLabel = typeof tab === 'string' ? tab : tab.label
        const tabIcon = typeof tab === 'string' ? undefined : tab.icon
        const tabDisabled = typeof tab === 'string' ? false : tab.disabled

        if (tabDisabled) {
          return (
            <Tooltip key={tabId}>
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    'cursor-default border-transparent border-b-2 pb-4 text-link-inactive',
                    'flex items-center gap-2',
                    className,
                  )}
                  disabled
                >
                  {tabIcon}
                  {tabLabel}
                  <Badge>Soon</Badge>
                </button>
              </TooltipTrigger>
              <TooltipContentShort>Coming soon!</TooltipContentShort>
            </Tooltip>
          )
        }

        return (
          <button
            key={tabId}
            className={cn(buttonStyles({ active: activeTab === tabId }), 'flex items-center gap-2', className)}
            onClick={() => setActiveTab(tabId)}
            disabled={activeTab === tabId}
          >
            {tabIcon}
            {tabLabel}
          </button>
        )
      })}
    </div>
  )
}

export interface TabSelectorLinkProps {
  tabs: readonly { label: string; href: string; inactive?: boolean; isNew?: boolean; postfix?: ReactNode }[]
  currentPath: string
  className?: string
}

export function TabSelectorLink({ tabs, currentPath, className }: TabSelectorLinkProps) {
  return (
    <div className={cn('flex gap-8', className)}>
      {tabs.map(({ label, href, inactive, postfix, isNew }) => (
        <div key={href} className="flex items-start gap-2">
          {inactive ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="cursor-default border-transparent border-b-2 pb-4 text-link-inactive">{label}</p>
              </TooltipTrigger>
              <TooltipContentShort>Coming soon!</TooltipContentShort>
            </Tooltip>
          ) : (
            <Link href={href} className={cn(buttonStyles({ active: currentPath === href }), 'flex items-center gap-2')}>
              {label}
              {isNew && (
                <GradientBadge>
                  <Typography variant="prompt" gradient>
                    New
                  </Typography>
                </GradientBadge>
              )}
            </Link>
          )}
          {postfix}
        </div>
      ))}
    </div>
  )
}
